'use client';

import React from 'react';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Clock, ArrowRight, ArrowUpRight } from 'lucide-react';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';
import Carousel, { type CarouselSlide } from './Carousel';
import { usePublishedArticles } from '../insights/usePublishedArticles';

/* Enough to be worth scrolling, few enough that the section stays a section.
   Anything past this is what the "View all articles" link is for. */
const MAX_ON_LANDING = 6;

export default function InsightsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  /* The same list /insights reads, rather than a copy of three of them frozen
     into this file. They were the first three of articlesData, so the section
     opens on exactly the articles it opened on before — but the rest are now
     reachable, and an article published from the dashboard appears here instead
     of only on the articles page. The hook returns the static set synchronously
     and prepends published ones once they arrive, so there is nothing to wait
     for on the first paint. */
  const articles = usePublishedArticles().slice(0, MAX_ON_LANDING);

  const slides: CarouselSlide[] = articles.map((article, i) => ({
    key: article.id || article.slug,
    title: article.title,
    number: String(i + 1).padStart(2, '0'),
    cardProps: {
      className: 'group',
      style: {
        background: 'var(--m-bg)',
        boxShadow: 'var(--m-lift-1), var(--m-lift-edge)',
        borderColor: 'rgba(var(--m-ink-rgb),0.13)',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        overflow: 'hidden',
      },
      onMouseEnter: (e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'var(--m-ink)';
      },
      onMouseLeave: (e) => {
        (e.currentTarget as HTMLElement).style.borderColor = 'rgba(var(--m-ink-rgb),0.13)';
      },
    },
    content: (
      <Link href={`/insights/${article.slug}`} className="flex h-full flex-col">
        {/* Header area */}
        <div
          className="relative p-6 pb-5"
          style={{
            background: 'rgba(var(--m-ink-rgb),0.02)',
            borderBottom: '1px solid rgba(var(--m-ink-rgb),0.06)',
          }}
        >
          <div className="flex items-start justify-between mb-6">
            <span
              className="inline-flex items-center px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest"
              style={{
                border: '1px solid rgba(var(--m-ink-rgb),0.12)',
                color: 'var(--m-muted)',
                letterSpacing: '0.12em',
                background: 'var(--m-bg)',
              }}
            >
              {article.category}
            </span>
            <span
              className="text-4xl font-mono font-bold select-none"
              style={{ color: 'rgba(var(--m-ink-rgb),0.06)', lineHeight: 1 }}
            >
              {String(i + 1).padStart(3, '0')}
            </span>
          </div>
          <h3
            className="text-base font-bold leading-snug transition-colors"
            style={{
              color: 'var(--m-ink)',
              fontFamily: "'Archivo Narrow','Roboto Condensed',sans-serif",
              lineHeight: 1.35,
            }}
          >
            {article.title}
          </h3>
        </div>

        <div className="p-6 flex flex-1 flex-col">
          <p
            className="text-sm leading-relaxed mb-6 flex-1"
            style={{ color: 'var(--m-ink-6)', lineHeight: 1.7 }}
          >
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-1.5 text-xs font-mono"
              style={{ color: 'var(--m-muted-3)' }}
            >
              <Clock className="w-3.5 h-3.5" />
              {article.readTime}
            </div>
            <div
              className="flex items-center gap-1.5 text-xs font-mono transition-colors group-hover:text-gray-900"
              style={{ color: 'var(--m-muted-3)' }}
            >
              Read article <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </Link>
    ),
  }));

  return (
    <section
      data-ground
      id="insights"
      className="section-rhythm"
      ref={ref}
      style={{
        background:
          'linear-gradient(180deg, transparent 0%, var(--m-panel) 7%, var(--m-panel) 93%, transparent 100%)',
      }}
    >
      <div className="m-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-14 gap-4"
        >
          <div>
            <span
              className="block mb-3 font-mono text-[11px] uppercase tracking-widest"
              style={{ color: 'var(--m-ink-6)', letterSpacing: '0.22em' }}
            >
              Insights &amp; Resources
            </span>
            <h2 className={SECTION_HEADING_CLASS} style={SECTION_HEADING_STYLE}>
              Practical guides for business growth
            </h2>
          </div>
          <Link
            href="/insights"
            className="px-5 py-2.5 flex items-center gap-2 flex-shrink-0 font-mono uppercase transition-all"
            style={{
              border: '1px solid rgba(var(--m-ink-rgb),0.15)',
              color: 'var(--m-ink-5)',
              fontSize: 11,
              letterSpacing: '0.1em',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'var(--m-ink)';
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--m-ink)';
              (e.currentTarget as HTMLElement).style.color = 'var(--m-on-ink)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(var(--m-ink-rgb),0.15)';
              (e.currentTarget as HTMLElement).style.color = 'var(--m-ink-5)';
            }}
          >
            View all articles <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </motion.div>

        {slides.length > 0 && (
          <Carousel
            id="insights-carousel"
            label="Insights and resources"
            noun="Article"
            isInView={isInView}
            staggerSeconds={0.1}
            slides={slides}
          />
        )}
      </div>
    </section>
  );
}
